import React, { useEffect, useState } from 'react';
import { CreateFormFields, ModalCreateFeedbackProps } from './types';
import { Button } from '@vtbhome/juicyfront';
import { Body, Footer } from './modal-create-feedback.styles';
import { Field, Form } from 'react-final-form';
import { CreateFormKeys } from '../../config/form-constants';
import { Typography } from '@shared/ui/typography/typography';
import { useFeedbackCreateRequestMutation } from '../../api/create-feedback-request-api';
import { CACHE_KEY_FEEDBACK_CREATE_REQUEST } from '../../config/api-constants';
import { Textarea } from '@shared/ui/textarea/textarea';
import { Select } from '@shared/ui/select/select';
import { DEFAULT_FEEDBACK_SUBJECTS_LIST_PARAMETERS } from '@entities/feedback/config/store-constants';
import { FeedbackSubjectListDataRequest } from '@entities/feedback/api/feedback-types';
import { useFeedbackSubjectsInfinityListQuery } from '@entities/feedback/api/feedback-api';
import { UserProductsListDataRequest } from '@entities/product/api/product-types';
import { DEFAULT_PRODUCTS_LIST_PARAMETERS } from '@entities/product/config/store-constants';
import { SELECT_INFINITY_PAGINATION_ITEMS_ON_PAGE } from '@shared/ui/select/constants';
import { useCurrentUserSelector } from '@entities/current-user/model/selectors';
import { FeedbackMigrateCodes } from '@shared/model/feedback';
import { OrdersListDataRequest } from '@entities/order/api/order-types';
import { DEFAULT_ORDERS_LIST_PARAMETERS } from '@entities/order/config/store-constants';
import { validate } from '../../lib/utils/form-validate/form-validate';
import { selectedOrderId } from '../../lib/utils/selected-order-id/selected-order-id';
import { selectedSubjectId } from '../../lib/utils/selected-subject-id/selected-subject-id';
import { selectedProductId } from '../../lib/utils/selected-product-id/selected-product-id';
import { useFeedbackProductSelect } from '../../lib/hooks/use-feedback-product-options/use-feedback-product-options';
import { feedbackSubjectDecorator } from '../../lib/utils/form-decorator/form-decorator';
import { useDispatch } from 'react-redux';
import { setIsShowCreateFeedbackRequest } from '@entities/feedback/model/slice';
import { useFeedbackOrderSelect } from '../../lib/hooks/use-feedback-order-options/use-feedback-order-options';
import { useFeedbackSubjectSelect } from '../../lib/hooks/use-feedback-subject-options/use-feedback-subject-options';
import { Modal } from '@shared/ui/modal/modal';
import { useLazyOrdersInfinityListQuery } from '@entities/order/api/order-api';
import { useLazyProductAvailableInfinityListQuery } from '@entities/product/api/product-api';
import { useResetModalForm } from '@shared/lib/hooks/use-reset-modal-form/use-reset-modal-form';

export const ModalCreateFeedback: React.FC<ModalCreateFeedbackProps> = ({ isShow }) => {
    const dispatch = useDispatch();
    const user = useCurrentUserSelector();
    const formRef = useResetModalForm<CreateFormFields>(isShow);

    /* Feedback subjects */
    const [feedbackSubjectsListParameters, setFeedbackSubjectsListParameters] =
        useState<FeedbackSubjectListDataRequest>({
            ...DEFAULT_FEEDBACK_SUBJECTS_LIST_PARAMETERS,
            take: SELECT_INFINITY_PAGINATION_ITEMS_ON_PAGE,
        });

    const {
        data: feedbackSubjectsList,
        isLoading: isFeedbackSubjectsListLoading,
        isFetching: isFeedbackSubjectsListFetching,
    } = useFeedbackSubjectsInfinityListQuery(feedbackSubjectsListParameters);

    const {
        feedbackSubjectOptions,
        feedbackSubjectEmptyOption,
        isShowEmptyLoaderFeedbackSubjects,
        infinityScrollFeedbackSubjectProps,
        feedbackSubjectInitialOptions,
        onSearchFeedbackSubjects,
    } = useFeedbackSubjectSelect({
        subjectsData: feedbackSubjectsList,
        isLoading: isFeedbackSubjectsListLoading,
        isFetching: isFeedbackSubjectsListFetching,
        setFeedbackSubjectsListParameters,
    });

    /* Products */
    const [productsListParameters, setProductsListParameters] = useState<UserProductsListDataRequest>({
        ...DEFAULT_PRODUCTS_LIST_PARAMETERS,
        userId: user?.id || null,
        take: SELECT_INFINITY_PAGINATION_ITEMS_ON_PAGE,
        order: 'created_at',
        sort: 'desc',
    });
    const [getProductsList, getProductsListResult] = useLazyProductAvailableInfinityListQuery();
    const productsList = getProductsListResult.data;
    const {
        productOptions,
        productEmptyOption,
        isShowEmptyLoaderProducts,
        infinityScrollProductsProps,
        onSearchProducts,
    } = useFeedbackProductSelect({
        productsData: productsList,
        isLoading: getProductsListResult.isLoading,
        isFetching: getProductsListResult.isFetching,
        setProductsListParameters,
    });

    /* Orders */
    const [ordersListParameters, setOrdersListParameters] = useState<OrdersListDataRequest>({
        ...DEFAULT_ORDERS_LIST_PARAMETERS,
        take: SELECT_INFINITY_PAGINATION_ITEMS_ON_PAGE,
        userId: user?.id,
        companyIds: user?.company?.id ? [user.company.id] : [],
    });
    const [getOrdersList, getOrdersListResult] = useLazyOrdersInfinityListQuery();
    const { orderOptions, orderEmptyOption, isShowEmptyLoaderOrders, infinityScrollOrdersProps, onSearchOrders } =
        useFeedbackOrderSelect({
            ordersData: getOrdersListResult.data,
            isLoading: getOrdersListResult.isLoading,
            isFetching: getOrdersListResult.isFetching,
            setOrdersListParameters,
        });

    /* Create feedback request */
    const [createFeedback, createFeedbackResult] = useFeedbackCreateRequestMutation({
        fixedCacheKey: CACHE_KEY_FEEDBACK_CREATE_REQUEST,
    });

    const isLoadingForm = createFeedbackResult.isLoading;

    const handleClose = () => {
        dispatch(setIsShowCreateFeedbackRequest(false));
    };

    const handleSubmit = async (fields: CreateFormFields) => {
        const productId = selectedProductId(fields[CreateFormKeys.Product], productsList?.items);
        const orderId = selectedOrderId(fields[CreateFormKeys.Order]);
        const subjectId = selectedSubjectId(fields[CreateFormKeys.Subject], feedbackSubjectsList?.items);
        const subject = fields[CreateFormKeys.Subject][0].value;
        await createFeedback({
            subjectId: subjectId || 0,
            itemId: subject === FeedbackMigrateCodes.Products ? productId : orderId,
            message: fields[CreateFormKeys.Message],
        });
    };

    useEffect(() => {
        getProductsList(productsListParameters);
    }, [productsListParameters]);

    useEffect(() => {
        if (ordersListParameters.companyIds.length) {
            getOrdersList(ordersListParameters);
        }
    }, [ordersListParameters]);

    useEffect(() => {
        setOrdersListParameters({
            ...ordersListParameters,
            userId: user?.id,
            companyIds: user?.company?.id ? [user.company.id] : [],
        });
    }, [user]);

    return (
        <Form
            decorators={[feedbackSubjectDecorator]}
            onSubmit={handleSubmit}
            validate={validate}
            render={(form) => {
                const selectedSubject = (form.values.subject || [])[0]?.value;
                formRef.current = form;
                return (
                    <Modal
                        isShow={isShow}
                        size='l'
                        showLine={false}
                        header={<Typography size='h6'>Создание обращения</Typography>}
                        footer={
                            <Footer>
                                <Button disabled={isLoadingForm} buttonType='light' onClick={handleClose}>
                                    Отменить
                                </Button>
                                <Button
                                    preloader={isLoadingForm}
                                    disabled={isLoadingForm}
                                    buttonType='primary'
                                    onClick={form.handleSubmit}>
                                    Подтвердить
                                </Button>
                            </Footer>
                        }
                        onClose={handleClose}>
                        <Body>
                            <Field
                                name={CreateFormKeys.Subject}
                                initialValue={feedbackSubjectInitialOptions}
                                render={({ input, meta }) => {
                                    return (
                                        <Select
                                            required
                                            isAsync
                                            clearable
                                            label='Тема обращения'
                                            placeholder='Выберите тему обращения'
                                            isShowPreloaderInList={isShowEmptyLoaderFeedbackSubjects}
                                            onSearch={onSearchFeedbackSubjects}
                                            infinityScrollProps={infinityScrollFeedbackSubjectProps}
                                            values={input.value || feedbackSubjectEmptyOption}
                                            options={feedbackSubjectOptions}
                                            error={meta.touched && meta.error}
                                            onChange={input.onChange}
                                        />
                                    );
                                }}
                            />
                            {selectedSubject === FeedbackMigrateCodes.Products && (
                                <Field
                                    name={CreateFormKeys.Product}
                                    render={({ input, meta }) => {
                                        return (
                                            <Select
                                                required
                                                isAsync
                                                clearable
                                                label='Продукт'
                                                placeholder='Выберите продукт'
                                                isShowPreloaderInList={isShowEmptyLoaderProducts}
                                                onSearch={onSearchProducts}
                                                infinityScrollProps={infinityScrollProductsProps}
                                                values={input.value || productEmptyOption}
                                                options={productOptions}
                                                error={meta.touched && meta.error}
                                                onChange={input.onChange}
                                            />
                                        );
                                    }}
                                />
                            )}

                            {selectedSubject === FeedbackMigrateCodes.Orders && (
                                <Field
                                    name={CreateFormKeys.Order}
                                    render={({ input, meta }) => {
                                        return (
                                            <Select
                                                required
                                                isAsync
                                                clearable
                                                label='Сделка'
                                                placeholder='Выберите сделку'
                                                isShowPreloaderInList={isShowEmptyLoaderOrders}
                                                onSearch={onSearchOrders}
                                                infinityScrollProps={infinityScrollOrdersProps}
                                                values={input.value || orderEmptyOption}
                                                options={orderOptions}
                                                error={meta.touched && meta.error}
                                                onChange={input.onChange}
                                            />
                                        );
                                    }}
                                />
                            )}
                            <Field
                                name={CreateFormKeys.Message}
                                render={({ input, meta }) => (
                                    <Textarea
                                        required
                                        label='Текст обращения'
                                        maxLength={255}
                                        placeholder='Напишите текст обращения'
                                        value={input.value}
                                        error={meta.touched && meta.error}
                                        onChange={input.onChange}
                                    />
                                )}
                            />
                        </Body>
                    </Modal>
                );
            }}
        />
    );
};
